import { SidenavLayout } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import AddemicLogoExpanded from '../assets/addemic/logo_expanded.svg';
import AddemicLogo from '../assets/addemic/logo_small.svg';
import { CampusFooter } from '../components/CampusFooter/CampusFooter';
import RoutesByLayout from '../components/routes/RoutesByLayout';
import { IS_ADDEMIC } from '../config/constants';
import { PathsLayouts } from '../config/routes';
import { useAuthLayout } from '../hooks/useAuthLayout';
import { useUserState } from '../hooks/useUserState';
import { TranslationsKeys } from '../locales/translations';
import { PermissionName } from '../types/Auth';
import { SidenavLinkWithPermission } from '../types/AuthNav';

export interface TopBarTitlesDict {
  [route: string]: string;
}

const purgeLinks = (
  links: SidenavLinkWithPermission[],
): SidenavLinkWithPermission[] => {
  links = links.filter((mainLink: any) => {
    return !mainLink?.disabled;
  });
  links = links.map((mainLink: any) => ({
    ...mainLink,
    links: mainLink?.links?.filter((subLink: any) => !subLink?.disabled),
  }));
  return links;
};

export default function AuthorizedLayout(props: { pathname: PathsLayouts }) {
  const { pathname } = props;
  const { t } = useTranslation();
  const { isLogged, isAuthorizedTo, userData } = useUserState();

  const ALL_SIDENAV_LINKS: SidenavLinkWithPermission[] = purgeLinks([
    {
      path: '/student-management/courses',
      name: t(`myCourses.navTitle`),
      icon: 'academic_offer',
    },
    {
      path: '/student-management/schedule/',
      name: t(`studentManagement.schedule`),
      icon: 'schedule',
    },
    {
      path: PathsLayouts.curricularAdvancement,
      name: t(`curricularAdvancement.navTitle`),
      icon: 'academic_progress',
    },
    {
      path: PathsLayouts.myCertificates,
      name: t(`reports.navTitle`),
      icon: 'certificates_requests',
    },
    {
      path: PathsLayouts.petitions,
      name: t(`petitions.navTitle`),
      icon: 'request',
    },
    {
      path: PathsLayouts.tuitionContinuity,
      name: t(`tuitionContinuity.navTitle`),
      icon: 'admision',
    },
    {
      path: PathsLayouts.degreeProcesses,
      name: t('navTitle', { ns: TranslationsKeys.DEGREE_POOCESSES_MAIN }),
      icon: 'study_plan',
    },
    {
      path: PathsLayouts.announcements,
      name: t(`terms:announcement.plural`),
      icon: 'alert',
    },
    {
      path: PathsLayouts.interimDegreeActivities,
      name: t(`interimDegreeActivities.navTitle`),
      icon: 'certification',
      requiredPermissions: [
        PermissionName.INTERIM_DEGREE_GRADUATION_PROCESS_ALL,
      ],
    },
  ]);

  // If the student is not regular, only show petitions and certificates in the sidebar
  const SIDENAV_LINKS =
    userData?.isRegular === false
      ? ALL_SIDENAV_LINKS.filter((link) => {
          const path = (link as any).path as string | undefined;
          return (
            path?.includes('/petitions') || path?.includes('/my-certificates')
          );
        })
      : ALL_SIDENAV_LINKS;

  const { getSidenavAuthorizedLinks } = useAuthLayout({
    SIDENAV_LINKS,
    isAuthorizedTo,
  });

  if (!isLogged) {
    return <Redirect to={PathsLayouts.auth} />;
  }

  return (
    <SidenavLayout
      links={getSidenavAuthorizedLinks()}
      logo={IS_ADDEMIC ? ADDEMIC_LOGOS : CAMPUS_LOGOS}
    >
      <div className="d-flex flex-column h-100">
        <div className="flex-grow-1">
          <RoutesByLayout pathLayout={pathname} />
        </div>
        <CampusFooter />
      </div>
    </SidenavLayout>
  );
}

const CAMPUS_LOGOS = {
  desktop: {
    src: '/tenant/logo_expanded.svg',
    fallbackSrc: '/tenant/logo_expanded.svg',
  },
  mobile: {
    src: '/tenant/logo_small.svg',
    fallbackSrc: '/tenant/logo_small.svg',
  },
};

const ADDEMIC_LOGOS = {
  desktop: {
    src: AddemicLogoExpanded,
    fallbackSrc: AddemicLogoExpanded,
  },
  mobile: {
    src: AddemicLogo,
    fallbackSrc: AddemicLogo,
  },
};
